import { useMemo, useRef, useState } from "react";
import { MessageSquareText, Briefcase, TrendingUp, ListTodo, MonitorSmartphone, PlugZap, Rss, Clock, Map as MapIcon, HeartPulse, Brain, CalendarClock } from "lucide-react";
import SageShell, { type Panel, type VerticalPanel, type GenMode, type SageShellHandle } from "./shell/SageShell";
import ChatPanel, { type ChatHandle } from "./panels/ChatPanel";
import ClientsPanel from "./panels/ClientsPanel";
import FinancePanel from "./panels/FinancePanel";
import WorkPanel from "./panels/WorkPanel";
import DevicesPanel from "./panels/DevicesPanel";
import ConnectionsPanel from "./panels/ConnectionsPanel";
import PersonalPanel from "./panels/PersonalPanel";
import FeedPanel from "./panels/FeedPanel";
import TimelinePanel from "./panels/TimelinePanel";
import MapPanel from "./panels/MapPanel";
import HealthPanel from "./panels/HealthPanel";
import KnowledgePanel from "./panels/KnowledgePanel";
import SchedulerPanel from "./panels/SchedulerPanel";
import MediaPanel from "./panels/MediaPanel";
import { chatStatus } from "./lib/chat";
import { useChatContext, getChatContext, setChatContext } from "./lib/chatContext";

interface Persona { id: string; shortName: string; color: string; prompt: string }
const PERSONAS: Persona[] = [
  { id: "prime", shortName: "SAGE", color: "#8fa876", prompt: "You are SAGE Prime, the core orchestrator persona — strategic, synthesizing, calm." },
  { id: "operator", shortName: "Operator", color: "#d49a6a", prompt: "You are the R&G Operator persona — restaurant-consulting focused, practical, Toast-savvy. Never suggest the Toast API." },
  { id: "analyst", shortName: "Analyst", color: "#d4b84b", prompt: "You are the Analyst persona — data-first, numbers, finance, trends, terse." },
  { id: "personal", shortName: "Personal", color: "#b0a898", prompt: "You are the Personal persona — Evan's private life assistant. Warm, candid." },
];

const SUGGESTIONS: Record<string, string[]> = {
  chat: ["What needs my attention today?", "Summarize this week", "Who haven't I contacted?"],
  clients: ["Which clients are at risk?", "Who has unbilled work?", "Top revenue clients"],
  finance: ["How's the trading engine?", "Revenue this month", "Any pending alerts?"],
  work: ["What's overdue?", "What's blocked?", "Next best action"],
  devices: ["Which devices are offline?", "Fleet status"],
  connections: ["Test all connections", "Which workers are down?"],
  personal: ["Recent messages", "Who texted me?"],
  feed: ["What's trending in restaurants?", "Summarize today's feed", "Anything relevant to my clients?"],
  timeline: ["What happened this week?", "Recent client calls", "Last contact with each client"],
  map: ["Who's near Hyannis?", "Clients I should visit", "Prospects by town"],
  health: ["How's my sleep trend?", "Steps this week", "Resting heart rate"],
  knowledge: ["Find my Toast menu notes", "What did I decide about housing?", "Crown & Anchor details"],
  scheduler: ["Plan my day", "What should I do first?", "Any scheduling conflicts?"],
  media: ["Summarize this recording", "What did I talk about?", "Recent lifelogs"],
};

export default function App() {
  const chatRef = useRef<ChatHandle>(null);
  const shellRef = useRef<SageShellHandle>(null);
  const [personaIdx, setPersonaIdx] = useState(0);
  const [thinking, setThinking] = useState(false);
  const [mode, setMode] = useState<GenMode | undefined>(undefined);
  const [activeId, setActiveId] = useState("chat");
  const [keysVersion, setKeysVersion] = useState(0); // re-read chat status after key save
  const persona = PERSONAS[personaIdx];

  const panels: Panel[] = useMemo(() => [
    { id: "feed", title: "Feed", icon: <Rss size={15} />, accentVar: "--biome-business", content: <FeedPanel /> },
    { id: "timeline", title: "Timeline", icon: <Clock size={15} />, accentVar: "--biome-tech", content: <TimelinePanel /> },
    { id: "work", title: "Work", icon: <ListTodo size={15} />, accentVar: "--biome-business", content: <WorkPanel /> },
    { id: "clients", title: "Clients", icon: <Briefcase size={15} />, accentVar: "--biome-business", content: <ClientsPanel /> },
    { id: "chat", title: "Brief", icon: <MessageSquareText size={15} />, accentVar: "--biome-tech", content: <ChatPanel ref={chatRef} personaPrompt={persona.prompt} /> },
    { id: "scheduler", title: "Agenda", icon: <CalendarClock size={15} />, accentVar: "--biome-business", content: <SchedulerPanel /> },
    { id: "finance", title: "Finance", icon: <TrendingUp size={15} />, accentVar: "--biome-finance", content: <FinancePanel /> },
    { id: "map", title: "Map", icon: <MapIcon size={15} />, accentVar: "--biome-gardener", content: <MapPanel /> },
    { id: "health", title: "Health", icon: <HeartPulse size={15} />, accentVar: "--biome-health", content: <HealthPanel /> },
    { id: "knowledge", title: "Knowledge", icon: <Brain size={15} />, accentVar: "--biome-mindvault", content: <KnowledgePanel /> },
    { id: "devices", title: "Devices", icon: <MonitorSmartphone size={15} />, accentVar: "--biome-tech", content: <DevicesPanel /> },
    { id: "connections", title: "Connections", icon: <PlugZap size={15} />, accentVar: "--biome-tech", content: <ConnectionsPanel onKeysChanged={() => setKeysVersion((v) => v + 1)} /> },
  ], [persona.prompt]);

  const panelAbove: VerticalPanel = { id: "personal", title: "Personal", accentVar: "--biome-personal", content: <PersonalPanel /> };
  const panelBelow: VerticalPanel = { id: "media", title: "Media", accentVar: "--biome-mindvault", content: <MediaPanel /> };

  const CHAT_INDEX = panels.findIndex((p) => p.id === "chat");
  const [, forceModel] = useState(0);
  void keysVersion;
  const model = chatStatus().model;
  const ctx = useChatContext();

  const handleSend = async (text: string) => {
    // Pull any referenced panel content into the central Chat pane (framework:
    // "the chat bar brings content from any page into the main central chat").
    const c = getChatContext();
    const composed = c ? `[Referring to ${c.label}]\n${c.detail}\n\n${text || "Discuss this."}` : text;
    shellRef.current?.goToId("chat");
    await new Promise((r) => setTimeout(r, 60));
    await chatRef.current?.send(composed);
    setChatContext(null);
    forceModel((v) => v + 1);
  };

  return (
    <SageShell
      ref={shellRef}
      panels={panels}
      initialPanelIndex={CHAT_INDEX}
      panelAbove={panelAbove}
      panelBelow={panelBelow}
      contextSource={ctx ? { label: ctx.label, onDismiss: () => setChatContext(null) } : undefined}
      onClearContext={() => setChatContext(null)}
      onSend={handleSend}
      disabled={chatRef.current?.generating}
      suggestions={SUGGESTIONS[activeId] ?? SUGGESTIONS.chat}
      onSuggestionClick={handleSend}
      activePersona={{ shortName: persona.shortName, color: persona.color }}
      onPersonaClick={() => setPersonaIdx((i) => (i + 1) % PERSONAS.length)}
      thinkingEnabled={thinking}
      onThinkingToggle={() => setThinking((t) => !t)}
      modelBadge={model}
      onModelClick={() => shellRef.current?.goToId("connections")}
      activeMode={mode}
      onModeToggle={(m) => setMode((cur) => (cur === m ? undefined : m))}
      onActivePanelChange={(_, id) => setActiveId(id)}
    />
  );
}
